import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Alert, Snackbar, CircularProgress, Skeleton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import IconButton from '@mui/material/IconButton';
import axios from 'axios';
import { getUserData } from '../../app/services/AuthService';
import StepsLayout from '../../app/common/StepsLayout';
import { downloadNtaiReport, getAvailableReports } from '../../app/api/agent';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noReports, setNoReports] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [errorMessage, setErrorMessage] = useState();
  const [error, setError] = useState(false);
  const user = getUserData();
  const env = process.env.REACT_APP_ENVIRONMENT || 'staging';

  useEffect(() => {
    fetchAvailableReports();
  }, []);

  const fetchAvailableReports = async () => {
    setLoading(true);
    setNoReports(false);

    try {
      const config = getAvailableReports(user.email);
      const response = await axios(config);

      if (
        response.data &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        setReports(response.data);
        setNoReports(false);
      } else {
        setReports([]);
        setNoReports(true);
      }
    } catch (error) {
      console.error('Error fetching available reports', error);
      setReports([]);
      setNoReports(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (reportPath, reportTitle) => {
    setDownloading((prev) => ({ ...prev, [reportPath]: true }));
    setErrorMessage(null);
    setError(false);

    try {
      const config = downloadNtaiReport(user.email, env);
      const response = await axios(config);

      if (response.data && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        throw new Error('No download URL received from server');
      }
    } catch (error) {
      console.error(`Error downloading ${reportTitle}`, error);
      setError(true);
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        setErrorMessage(error.response.data.message);
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setErrorMessage(error.response.data.message);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred, please try again later');
      }
    } finally {
      setDownloading((prev) => ({ ...prev, [reportPath]: false }));
    }
  };

  const handleClose = () => {
    setErrorMessage(null);
    setError(false);
  };

  const action = (
    <React.Fragment>
      <Button color="secondary" size="small" onClick={handleClose}>
        CLOSE
      </Button>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  return (
    <StepsLayout>
      <Box display="flex" alignItems="center" mb={3}>
        <DownloadIcon sx={{ fontSize: 84, mr: 2, color: '#85b4cd' }} />
        <Typography component="h1" variant="h3" className="steps-header">
          Reports
        </Typography>
      </Box>

      <Box
        style={{
          backgroundColor: 'white',
          padding: '50px 30px 30px 30px',
          borderRadius: '10px',
        }}
        sx={{ mt: 3 }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontWeight: 'bold', mb: 3 }}
        >
          Available Reports
        </Typography>

        {loading ? (
          <Box>
            <Skeleton
              variant="rectangular"
              height={180}
              sx={{ borderRadius: '10px', mb: 2 }}
            />
            <Skeleton variant="text" width="60%" height={30} />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        ) : noReports ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              textAlign: 'center',
            }}
          >
            <AssignmentIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ color: '#666', fontWeight: 'normal' }}
            >
              No reports available for you at this time.
            </Typography>
          </Box>
        ) : (
          reports.map((report, index) => (
            <Box
              key={index}
              sx={{
                border: '2px solid #85b4cd',
                borderRadius: '10px',
                padding: '30px',
                backgroundColor: '#f9fcfe',
                mb: 3,
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                {report.title}
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: '#555' }}>
                Your comprehensive {report.title} is ready for download. This
                report contains detailed insights from your health assessment.
              </Typography>

              <Button
                disabled={downloading[report.path]}
                onClick={() => handleDownload(report.path, report.title)}
                variant="contained"
                startIcon={
                  downloading[report.path] ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                style={{
                  fontSize: '17px',
                  borderRadius: '9px',
                  backgroundColor: downloading[report.path]
                    ? '#ccc'
                    : '#85b4cd',
                  padding: '12px 30px',
                  marginTop: '10px',
                }}
                sx={{ '&:hover': { backgroundColor: '#6a9ab8' } }}
              >
                {downloading[report.path]
                  ? 'Downloading...'
                  : `Download ${report.title}`}
              </Button>
            </Box>
          ))
        )}

        {!loading && !noReports && (
          <Typography
            variant="body2"
            sx={{ color: '#888', fontStyle: 'italic', mt: 2 }}
          >
            If you experience any issues downloading your report, please contact
            support.
          </Typography>
        )}
      </Box>

      <Snackbar
        open={error}
        autoHideDuration={6000}
        onClose={handleClose}
        message={errorMessage}
        action={action}
      >
        <Alert
          onClose={handleClose}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </StepsLayout>
  );
};

export default Reports;
